import { type CommentBlock, type CommentUser, isUnsigned } from "@core-ui/CommentBlock";
import useWatch from "@core-ui/hooks/useWatch";
import t from "@ext/localization/locale/translate";
import { CommentContent } from "@ext/markdown/elements/comment/edit/components/Popover/CommentContent";
import { CommentHeader } from "@ext/markdown/elements/comment/edit/components/Popover/CommentHeader";
import { CommentMessage } from "@ext/markdown/elements/comment/edit/components/Popover/CommentMessage";
import GlobalEditorIsEditable from "@ext/markdown/elements/comment/edit/logic/GlobalIsEditable";
import type { JSONContent } from "@tiptap/core";
import { ScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import { useCallback, useContext, useEffect, useRef } from "react";

interface CommentProps {
	commentId: string;
	data: CommentBlock;
	user: CommentUser;
	onClose: () => void;
	onDelete: () => void;
	onCreate: (comment: CommentBlock) => void;
	onAddAnswer: (comment: CommentBlock, hide: boolean) => void;
	onDeleteAnswer: (comment: CommentBlock) => void;
}

export const Comment = (props: CommentProps) => {
	const {
		commentId,
		data,
		user,
		onClose,
		onDelete,
		onCreate,
		onAddAnswer,
		onDeleteAnswer: onDeleteAnswerProp,
	} = props;
	const dataRef = useRef<CommentBlock>(data);
	const scrollShadowContainerRef = useRef<HTMLDivElement>(null);
	const isNewComment = !data?.comment;
	const isEditable = useContext(GlobalEditorIsEditable);
	const containerRef = useRef<HTMLDivElement>(null);

	useWatch(() => {
		dataRef.current = data;
	}, [data]);

	useEffect(() => {
		const scrollableElement = scrollShadowContainerRef.current;
		if (!scrollableElement) return;
		if (!scrollableElement?.scrollTo) return;

		scrollableElement.scrollTo({
			top: scrollableElement.scrollHeight,
		});
	}, []);

	const onCreateComment = useCallback(
		(_: number, content: JSONContent[]) => {
			onCreate({ comment: { dateTime: new Date().toISOString(), user, content } });
		},
		[onCreate, user],
	);

	const onEditMainComment = useCallback(
		(_: number, content: JSONContent[]) => {
			const data = dataRef.current;

			onAddAnswer(
				{
					...data,
					comment: { ...data.comment, content },
				},
				false,
			);
		},
		[onAddAnswer],
	);

	const onAddCommentAnswer = useCallback(
		(index: number, content: JSONContent[], hide: boolean = true) => {
			const data = dataRef.current;
			if (index > data.answers.length) {
				return onAddAnswer(
					{
						...data,
						answers: [...data.answers, { dateTime: new Date().toISOString(), user, content }],
					},
					true,
				);
			}

			onAddAnswer(
				{
					...data,
					answers: data.answers.map((answer, i) => (i + 1 === index ? { ...answer, content } : answer)),
				},
				hide,
			);
		},
		[onAddAnswer, user],
	);

	const onDeleteAnswer = useCallback(
		(index: number) => {
			const data = dataRef.current;
			onDeleteAnswerProp({
				...data,
				answers: data.answers.filter((_, i) => i + 1 !== index),
			});
		},
		[onDeleteAnswerProp],
	);

	if (isNewComment) {
		return (
			<CommentContent aria-label={t("comment")} data-qa="qa-add-comment" ref={containerRef} role="dialog">
				<CommentMessage
					autofocus
					className="p-2 pl-4 border-t-0 border-b-0"
					editable
					index={0}
					isCurrentUser
					onConfirm={onCreateComment}
					showAvatar={false}
					showName={false}
					user={user}
				/>
			</CommentContent>
		);
	}

	const isMine = (author: CommentUser) => isEditable && (isUnsigned(author) || author.mail === user.mail);

	return (
		<CommentContent
			aria-label={t("comment")}
			header={
				<CommentHeader
					commentId={commentId}
					onClose={onClose}
					onResolve={onDelete}
					renderDeleteIcon={isEditable}
				/>
			}
			ref={containerRef}
			role="dialog"
		>
			<ScrollShadowContainer className="min-h-0 flex-1 overflow-x-hidden" ref={scrollShadowContainerRef}>
				<div className="flex flex-col">
					{data.comment && (
						<CommentMessage
							assistedByAgent={data.comment.assistedByAgent}
							containerRef={containerRef}
							content={data.comment.content}
							date={data.comment.dateTime}
							index={0}
							isCurrentUser={isMine(data.comment.user)}
							last={!data?.answers?.length}
							onConfirm={onEditMainComment}
							user={data.comment.user}
						/>
					)}
					{data.answers.map((answer, index) => (
						<CommentMessage
							assistedByAgent={answer.assistedByAgent}
							containerRef={containerRef}
							content={answer.content}
							date={answer.dateTime}
							index={index + 1}
							isCurrentUser={isMine(answer.user)}
							key={answer.dateTime}
							last={index === data.answers.length - 1}
							onConfirm={onAddCommentAnswer}
							onDelete={onDeleteAnswer}
							user={answer.user}
						/>
					))}
				</div>
			</ScrollShadowContainer>
			{isEditable && (
				<CommentMessage
					className="shrink-0 bg-secondary-bg/50"
					containerRef={containerRef}
					editable
					index={data.answers.length + 1}
					isCurrentUser
					last
					onConfirm={onAddCommentAnswer}
					showName={false}
					user={user}
				/>
			)}
		</CommentContent>
	);
};
